import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowUpRight } from "lucide-react";
import type { BioLink } from "@/hooks/useBioLinks";
import { extractYouTubeId, buildYouTubeEmbedUrl } from "@/lib/youtube";
import {
  resolveBioTheme,
  pickPrimaryLinkId,
  socialAbbr,
  type BioThemeInput,
  type BioTheme,
} from "@/lib/bioTheme";
import { getBioIcon, type BioIconKey } from "@/lib/bioIcons";
import { cn } from "@/lib/utils";

export interface BioViewProfile extends BioThemeInput {
  display_name: string;
  tagline?: string | null;
  avatar_url?: string | null;
}

/**
 * The /bio page as designed in the redesign prototype ("Paginas do Cliente",
 * tela Bio). Pure presentation: the public page and the admin preview both
 * render this, so what the operator sees in the panel is what ships.
 * Every colour comes from resolveBioTheme → CSS vars on the wrapper.
 */
export function BioView({
  profile,
  links,
  onLinkClick,
  preview = false,
}: {
  profile: BioViewProfile;
  links: BioLink[];
  onLinkClick?: (link: BioLink) => void;
  /** Admin preview: no navigation, no min-height, no tracking. */
  preview?: boolean;
}) {
  const theme = resolveBioTheme(profile);
  const socialLinks = links.filter((l) => l.kind === "social");
  const regularLinks = links.filter((l) => l.kind === "link" || l.kind === "video");
  const blogPosts = links.filter(
    (l) => l.kind === "blog_post" && l.blog_posts && l.blog_posts.published,
  );
  const primaryLinkId = pickPrimaryLinkId(regularLinks);

  const click = (link: BioLink) => (e: React.MouseEvent) => {
    if (preview) e.preventDefault();
    onLinkClick?.(link);
  };

  return (
    <div
      className={cn("relative flex flex-col items-center font-sans", !preview && "min-h-screen")}
      style={{ ...(theme.vars as CSSProperties), ...theme.background, color: "var(--bio-ink)" }}
    >
      {theme.overlayOpacity !== null && (
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "#000", opacity: theme.overlayOpacity }}
        />
      )}

      <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center px-6 pb-12 pt-9 text-center">
        {profile.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={profile.display_name}
            className="h-[86px] w-[86px] rounded-full border-[3px] object-cover"
            style={{ borderColor: "var(--bio-surface)", boxShadow: "var(--bio-shadow)" }}
          />
        ) : (
          <div
            className="h-[86px] w-[86px] rounded-full border-[3px]"
            style={{
              borderColor: "var(--bio-surface)",
              backgroundColor: "var(--bio-line)",
              boxShadow: "var(--bio-shadow)",
            }}
          />
        )}
        <h1
          className="mt-4 font-display text-[21px] font-bold tracking-[-.02em]"
          style={{ color: "var(--bio-ink)" }}
        >
          {profile.display_name}
        </h1>
        {profile.tagline && (
          <p className="mt-[7px] text-[13.5px] leading-[1.6]" style={{ color: "var(--bio-ink2)" }}>
            {profile.tagline}
          </p>
        )}

        <div className="mt-[26px] flex w-full flex-col gap-2.5">
          {regularLinks.map((link) => (
            <BioLinkItem
              key={link.id}
              link={link}
              primary={link.id === primaryLinkId}
              theme={theme}
              onClick={click(link)}
            />
          ))}
          {regularLinks.length === 0 && blogPosts.length === 0 && (
            <p className="text-center text-sm" style={{ color: "var(--bio-ink2)" }}>
              Nenhum link disponível no momento.
            </p>
          )}
        </div>

        <BioBlogGrid posts={blogPosts} theme={theme} onClick={click} />

        {socialLinks.length > 0 && (
          <div className="mt-7 flex flex-wrap justify-center gap-[9px]">
            {socialLinks.map((link) => {
              const Icon =
                theme.socialStyle === "icon"
                  ? getBioIcon((link.icon as BioIconKey) || "link").Icon
                  : null;
              return (
                <a
                  key={link.id}
                  href={link.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.title || "Rede social"}
                  title={link.title || undefined}
                  onClick={click(link)}
                  className="flex h-[42px] w-[42px] items-center justify-center rounded-full border text-xs font-bold transition-opacity hover:opacity-80"
                  style={{
                    borderColor: "var(--bio-line)",
                    backgroundColor: "var(--bio-surface)",
                    color: "var(--bio-ink2)",
                  }}
                >
                  {Icon ? <Icon className="h-4 w-4" /> : socialAbbr(link)}
                </a>
              );
            })}
          </div>
        )}

        {theme.showFooter && theme.footerText && (
          <p className="mt-[26px] text-[11.5px]" style={{ color: "var(--bio-ink3)" }}>
            {theme.footerText}
          </p>
        )}
      </div>
    </div>
  );
}

function BioLinkItem({
  link,
  primary,
  theme,
  onClick,
}: {
  link: BioLink;
  primary: boolean;
  theme: BioTheme;
  onClick: (e: React.MouseEvent) => void;
}) {
  if (link.kind === "video" && link.video_url) {
    return (
      <div className={cn("w-full overflow-hidden", theme.radiusClass)}>
        {link.title && (
          <p className="mb-2 text-center text-sm" style={{ color: "var(--bio-ink2)" }}>
            {link.title}
          </p>
        )}
        <BioVideoEmbed
          videoUrl={link.video_url}
          provider={link.video_provider}
          radiusClass={theme.radiusClass}
        />
      </div>
    );
  }

  const cardStyle: CSSProperties = primary
    ? {
        ...theme.primaryStyle,
        borderColor: theme.primaryStyle.border ? undefined : "var(--bio-accent)",
        boxShadow: "var(--bio-shadow)",
      }
    : {
        backgroundColor: "var(--bio-surface)",
        borderColor: "var(--bio-line)",
        color: "var(--bio-ink)",
        boxShadow: "var(--bio-shadow)",
      };

  return (
    <a
      href={link.url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-[13px] border px-[18px] py-4 text-left transition-transform hover:-translate-y-0.5",
        theme.radiusClass,
      )}
      style={cardStyle}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">{link.title || link.url}</span>
        {link.description && (
          <span
            // opacity-85, não 75 como no protótipo: 4,8:1 sobre o roxo do card em destaque (AA)
            className={cn("mt-0.5 block truncate text-[11.5px]", primary && "opacity-85")}
            style={primary ? undefined : { color: "var(--bio-ink3)" }}
          >
            {link.description}
          </span>
        )}
      </span>
      <ArrowUpRight className="h-[17px] w-[17px] shrink-0" />
    </a>
  );
}

function BioBlogGrid({
  posts,
  theme,
  onClick,
}: {
  posts: BioLink[];
  theme: BioTheme;
  onClick: (link: BioLink) => (e: React.MouseEvent) => void;
}) {
  if (posts.length === 0) return null;

  if (posts.length === 1) {
    return (
      <div className="mt-4 w-full">
        <BioBlogCard link={posts[0]} theme={theme} onClick={onClick(posts[0])} />
      </div>
    );
  }

  return (
    <div className="mt-4 w-full columns-2 gap-3">
      {posts.map((post) => (
        <div key={post.id} className="mb-3 break-inside-avoid">
          <BioBlogCard link={post} theme={theme} onClick={onClick(post)} />
        </div>
      ))}
    </div>
  );
}

function BioBlogCard({
  link,
  theme,
  onClick,
}: {
  link: BioLink;
  theme: BioTheme;
  onClick: (e: React.MouseEvent) => void;
}) {
  if (!link.blog_posts) return null;

  return (
    <Link
      to={`/blog/${link.blog_posts.slug}`}
      onClick={onClick}
      className={cn("block w-full overflow-hidden border text-left", theme.radiusClass)}
      style={{
        backgroundColor: "var(--bio-surface)",
        borderColor: "var(--bio-line)",
        color: "var(--bio-ink)",
        boxShadow: "var(--bio-shadow)",
      }}
    >
      {link.blog_posts.cover_image_url ? (
        <img src={link.blog_posts.cover_image_url} alt="" className="block h-auto w-full" />
      ) : (
        <div className="aspect-video w-full" style={{ backgroundColor: "var(--bio-line)" }} />
      )}
      <div className="p-3">
        <span className="line-clamp-2 text-sm font-medium leading-snug">
          {link.blog_posts.title}
        </span>
      </div>
    </Link>
  );
}

function BioVideoEmbed({
  videoUrl,
  provider,
  radiusClass,
}: {
  videoUrl: string;
  provider: BioLink["video_provider"];
  radiusClass: string;
}) {
  const youtubeId = provider === "youtube" ? extractYouTubeId(videoUrl) : null;

  if (provider === "youtube" && youtubeId) {
    return (
      <div className={cn("relative aspect-video w-full overflow-hidden", radiusClass)}>
        <iframe
          src={buildYouTubeEmbedUrl(youtubeId)}
          title="Vídeo incorporado"
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (provider === "instagram" || provider === "tiktok") {
    return <OEmbedVideo videoUrl={videoUrl} provider={provider} />;
  }

  return (
    <a
      href={videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm underline"
      style={{ color: "var(--bio-accent)" }}
    >
      Abrir vídeo
    </a>
  );
}

const SCRIPT_SRC: Record<"instagram" | "tiktok", string> = {
  instagram: "https://www.instagram.com/embed.js",
  tiktok: "https://www.tiktok.com/embed.js",
};

function OEmbedVideo({
  videoUrl,
  provider,
}: {
  videoUrl: string;
  provider: "instagram" | "tiktok";
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(false);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    fetch(`${supabaseUrl}/functions/v1/bio-oembed?url=${encodeURIComponent(videoUrl)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.error) throw new Error(data.error);
        setHtml(data.html);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [videoUrl]);

  useEffect(() => {
    if (!html) return;

    if (provider === "tiktok") {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC.tiktok;
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }

    const existing = document.getElementById("instagram-embed-script");
    const process = () => {
      const ig = (window as unknown as { instgrm?: { Embeds: { process: () => void } } }).instgrm;
      ig?.Embeds.process();
    };
    if (existing) {
      process();
    } else {
      const script = document.createElement("script");
      script.id = "instagram-embed-script";
      script.src = SCRIPT_SRC.instagram;
      script.async = true;
      script.onload = process;
      document.body.appendChild(script);
    }
  }, [html, provider]);

  if (error) {
    return (
      <a
        href={videoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm underline"
        style={{ color: "var(--bio-accent)" }}
      >
        Abrir vídeo no {provider === "instagram" ? "Instagram" : "TikTok"}
      </a>
    );
  }

  if (!html) {
    return (
      <div
        className="flex aspect-square w-full items-center justify-center rounded-xl"
        style={{ backgroundColor: "var(--bio-line)" }}
      >
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--bio-ink3)" }} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
